import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import type {
  Airline,
  BookedFlightItineraryWithDepartureTime,
} from "@b2bportal/air-booking-api";
import type {
  Fare,
  FlightsFareSlice,
  Slice,
  Trip,
  TripSlice,
} from "@b2bportal/air-shopping-api";
import { ChatPropertiesType } from "@b2bportal/chat-api";
import type { IContactInfo } from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useChatSupport } from "@hopper-b2b/self-serve";
import {
  DisruptionExerciseProductType,
  DisruptionExerciseProgress,
  DisruptionProtectionType,
  type FiatPrice,
  type TripDetails,
} from "@hopper-b2b/types";
import { B2BLoadingPopup } from "@hopper-b2b/ui";
import {
  getEnvVariables,
  parsePhoneNumber,
  useDeviceTypes,
} from "@hopper-b2b/utilities";

import { ClientContext } from "../../../../App";
import {
  populateTripQueryParams,
  resetDisruptionExerciseState,
  setDisruptionExerciseProgress,
} from "../../../TripsList/actions/actions";
import {
  getDisruptionExerciseProductType,
  getDisruptionExerciseProgress,
} from "../../../TripsList/reducer/selectors";
import { DisruptionContactInfoForm } from "./components/DisruptionContactInfoForm";
import { DisruptionFlightSearch } from "./components/DisruptionFlightSearch";
import { DisruptionRebookConfirmation } from "./components/DisruptionRebookConfirmation";
import { DisruptionRebookReview } from "./components/DisruptionRebookReview";
import { DisruptionRefund } from "./components/DisruptionRefund";
import { FlightSelect } from "./components/FlightSelect";
import { LandingPage } from "./components/LandingPage";
import "./styles.scss";

export interface IDisruptionExerciseModalProps {
  flight: BookedFlightItineraryWithDepartureTime;
  onClose?: () => void;
}

export const DisruptionExerciseModal = ({
  flight,
  onClose,
}: IDisruptionExerciseModalProps) => {
  const { productSupportChat } = useChatSupport();
  const clientContext = useContext(ClientContext);
  const { matchesMobile } = useDeviceTypes();
  const dispatch = useDispatch();
  const { t, brand } = useI18nContext();

  const [selectedFlight, setSelectedFlight] = useState<{
    fare: Fare;
    trip: Trip;
    tripSlice: Slice;
    fareSlice: FlightsFareSlice;
    tripDetails: TripDetails;
  }>(null);
  const [flightSearch, setFlightSearch] = useState<{
    origin: string;
    destination: string;
    tripSlice: TripSlice;
  }>({ origin: "", destination: "", tripSlice: null });
  const [flightResultAirlines, setFlightResultAirlines] = useState<{
    [key: string]: Airline;
  }>(null);

  const [refundLoading, setRefundLoading] = useState<boolean>(false);
  const [reimbursementAmount, setReimbursementAmount] =
    useState<FiatPrice | null>(null);

  const [contactInfo, setContactInfo] = useState<IContactInfo>({
    email: "",
    phoneNumber: "",
    countryCode: "",
  });

  const disruptionExerciseProgress = useSelector(getDisruptionExerciseProgress);
  const disruptionProductType = useSelector(getDisruptionExerciseProductType);
  const history = useHistory();

  const disableFloatingButton =
    getEnvVariables("clientName") !== "uber" && !matchesMobile;

  useEffect(() => {
    if (
      clientContext &&
      !contactInfo.phoneNumber &&
      !contactInfo.email &&
      !contactInfo.countryCode
    ) {
      const parsedPhone = parsePhoneNumber(
        clientContext?.sessionInfo?.userInfoResponse?.mobileNumber
      );
      const initialContactInfo = {
        countryCode: contactInfo.countryCode
          ? contactInfo.countryCode
          : `+${parsedPhone.countryCode || brand.preferredAreaCode}`,
        phoneNumber: contactInfo.phoneNumber
          ? contactInfo.phoneNumber
          : parsedPhone.nationalNumber,
        email: contactInfo.email
          ? contactInfo.email
          : clientContext?.sessionInfo?.userInfoResponse?.email || "",
      };
      setContactInfo(initialContactInfo);
    }
  }, [brand, clientContext, contactInfo]);

  useEffect(() => {
    return () => {
      dispatch(resetDisruptionExerciseState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading =
    disruptionExerciseProgress === DisruptionExerciseProgress.Pending;

  const contractId = useMemo(() => {
    return flight.ancillaries[
      disruptionProductType === DisruptionExerciseProductType.ScheduleChange
        ? DisruptionProtectionType.Delay
        : DisruptionProtectionType.MissedConnection
    ]?.id?.policyId;
  }, [disruptionProductType, flight.ancillaries]);

  const airlineCodes = useMemo(() => {
    if (flight?.bookedItinerary?.travelItinerary?.slices) {
      return flight?.bookedItinerary?.travelItinerary?.slices.flatMap(
        (slice) => {
          return slice?.segments?.map((segment) => {
            return segment?.operatingAirline?.code;
          });
        }
      );
    }
    return [];
  }, [flight]);

  useEffect(() => {
    if (flightSearch.tripSlice) {
      dispatch(
        setDisruptionExerciseProgress(DisruptionExerciseProgress.FlightSearch)
      );
      dispatch(populateTripQueryParams(history));
    }
  }, [dispatch, flightSearch, history]);

  const onFlightSelectContinue = useCallback(
    (props: { origin: string; destination: string; tripSlice: TripSlice }) => {
      setFlightSearch(props);
    },
    []
  );

  const handleFlightSelect = useCallback(
    (props: {
      fare: Fare;
      trip: Trip;
      tripSlice: Slice;
      fareSlice: FlightsFareSlice;
      tripDetails: TripDetails;
    }) => {
      if (selectedFlight?.fare.id === props.fare.id) {
        setSelectedFlight(null);
      } else {
        setSelectedFlight({ ...props });
      }
    },
    [selectedFlight]
  );

  const openSupportChat = useCallback(
    (flightDetails: BookedFlightItineraryWithDepartureTime) => {
      productSupportChat(
        flightDetails.bookedItinerary.id,
        ChatPropertiesType.Air,
        "Disruption"
      );
    },
    [productSupportChat]
  );

  const errorModalPrimaryOnClick = useCallback(() => {
    dispatch(resetDisruptionExerciseState());
  }, [dispatch]);

  const errorModalSecondaryOnClick = useCallback(
    (flightDetails: BookedFlightItineraryWithDepartureTime) => {
      openSupportChat(flightDetails);
      dispatch(resetDisruptionExerciseState());
    },
    [dispatch, openSupportChat]
  );

  const updateContactInfo = useCallback((changeInfo: IContactInfo) => {
    const updatedContactInfo = {
      email: changeInfo.email,
      phoneNumber: changeInfo.phoneNumber,
      countryCode: changeInfo.countryCode,
    };
    setContactInfo(updatedContactInfo);
  }, []);

  return (
    <div className="disruption-exercise">
      <B2BLoadingPopup
        open={loading}
        fullScreen={true}
        message={t("disruptionExercise.loadingMessage")}
        popupSize="mobile"
      />
      {[
        DisruptionExerciseProgress.RefundReview,
        DisruptionExerciseProgress.RefundConfirmation,
      ].includes(disruptionExerciseProgress) && (
        <DisruptionRefund
          flight={flight}
          confirmation={
            disruptionExerciseProgress ===
            DisruptionExerciseProgress.RefundConfirmation
          }
          contractId={contractId}
          itineraryId={flight.bookedItinerary.id}
          loading={refundLoading}
          setLoading={setRefundLoading}
          reimbursementAmount={reimbursementAmount}
          errorModalPrimaryOnClick={errorModalPrimaryOnClick}
          errorModalSecondaryOnClick={errorModalSecondaryOnClick}
          disableFloatingButton={disableFloatingButton}
          onDone={onClose}
        />
      )}

      {disruptionExerciseProgress ===
        DisruptionExerciseProgress.LandingPage && (
        <LandingPage
          contractId={contractId}
          airlineCodes={airlineCodes}
          loading={refundLoading}
          setLoading={setRefundLoading}
          setReimbursementAmount={setReimbursementAmount}
          disableFloatingButton={disableFloatingButton}
        />
      )}

      {disruptionExerciseProgress ===
        DisruptionExerciseProgress.FlightSelect && (
        <FlightSelect
          contractId={contractId}
          flight={flight}
          onFlightSelectContinue={onFlightSelectContinue}
          disableFloatingButton={disableFloatingButton}
        />
      )}

      {disruptionExerciseProgress ===
        DisruptionExerciseProgress.FlightSearch && (
        <DisruptionFlightSearch
          {...flightSearch}
          contractId={contractId}
          handleFlightSelect={handleFlightSelect}
          setFlightResultAirlines={setFlightResultAirlines}
          disableFloatingButton={disableFloatingButton}
        />
      )}
      {disruptionExerciseProgress ===
        DisruptionExerciseProgress.ContactInfo && (
        <DisruptionContactInfoForm
          contactInfo={contactInfo}
          updateContactInfo={updateContactInfo}
          disableFloatingButton={disableFloatingButton}
        />
      )}
      {disruptionExerciseProgress ===
        DisruptionExerciseProgress.RebookReview && (
        <DisruptionRebookReview
          selectedRebookFlight={selectedFlight}
          airlineMap={flightResultAirlines}
          disableFloatingButton={disableFloatingButton}
        />
      )}
      {disruptionExerciseProgress ===
        DisruptionExerciseProgress.ConfirmationPage && (
        <DisruptionRebookConfirmation
          flight={flight}
          contractId={contractId}
          selectedFlight={selectedFlight}
          errorModalPrimaryOnClick={errorModalPrimaryOnClick}
          errorModalSecondaryOnClick={errorModalSecondaryOnClick}
          contactInfo={contactInfo}
          disableFloatingButton={disableFloatingButton}
          onDone={onClose}
        />
      )}
    </div>
  );
};
